/**
 * PDF Vision Service - Parse PDF scan using GPT-4 Vision
 * Giống NotebookLM: hiểu text, tables, images, formulas
 */

import { aiHeaders, handleAIError } from '../ai/aiConfig'
import * as pdfjsLib from 'pdfjs-dist'
import type { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api'

// Configure worker
if (typeof window !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`
}

export interface PDFVisionResult {
  success: boolean
  content?: string
  pageContents?: PageContent[]
  error?: string
  tokensUsed?: number
}

export interface PageContent {
  pageNumber: number
  content: string
  hasImages: boolean
  hasTables: boolean
  hasFormulas: boolean
}

import { API_CONFIG } from '../../config/api.config'

class PDFVisionService {
  private model: string = 'gpt-4o' // GPT-4 với vision capabilities

  constructor() {
    // API key được quản lý bởi proxy - không cần trong client
    const proxyUrl = API_CONFIG.openai.proxyUrl
    if (!proxyUrl) {
      console.warn('⚠️ CẢNH BÁO: OpenAI Proxy URL chưa được cấu hình!')
    }
  }

  /**
   * Convert PDF page to image (base64)
   */
  private async convertPageToImage(
    pdf: PDFDocumentProxy,
    pageNumber: number,
    scale: number = 2.0
  ): Promise<string> {
    const page = await pdf.getPage(pageNumber)
    const viewport = page.getViewport({ scale })

    // Create canvas
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Cannot get canvas context')

    canvas.width = viewport.width
    canvas.height = viewport.height

    // Render page to canvas
    await page.render({
      canvasContext: context,
      viewport: viewport
    }).promise

    // Convert to base64
    return canvas.toDataURL('image/jpeg', 0.85).split(',')[1]
  }

  /**
   * Analyze PDF page image with GPT-4 Vision
   */
  private async analyzePageWithVision(
    imageBase64: string,
    pageNumber: number
  ): Promise<PageContent> {
    try {
      // Sử dụng proxy để bảo mật API key
      const proxyUrl = API_CONFIG.openai.proxyUrl
      if (!proxyUrl) {
        throw new Error('OpenAI Proxy URL chưa được cấu hình')
      }

      console.log(`🔐 Analyzing page ${pageNumber} via proxy: ${proxyUrl}`)

      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...aiHeaders('text')
        },
        body: JSON.stringify({
          model: this.model,
          messages: [
            {
              role: 'user',
              content: [
                {
                  type: 'text',
                  text: `Bạn là chuyên gia Vật lí. Hãy phân tích trang tài liệu này và extract TẤT CẢ thông tin:

**YÊU CẦU:**
1. **Text**: Trích xuất toàn bộ text (bao gồm cả text trong hình ảnh)
2. **Bảng biểu**: Mô tả và extract data từ bảng
3. **Hình ảnh**: Mô tả các hình vẽ, sơ đồ, đồ thị
4. **Công thức**: Extract công thức Vật lí (viết dạng text, ví dụ: F = ma)
5. **Số liệu**: Extract tất cả số liệu quan trọng

**FORMAT OUTPUT:**
Trả về text thuần dễ đọc, bao gồm:
- Text chính
- [BẢNG: mô tả bảng và data]
- [HÌNH: mô tả hình vẽ]
- [CÔNG THỨC: liệt kê công thức]

Hãy extract chi tiết và chính xác nhất có thể.`
                },
                {
                  type: 'image_url',
                  image_url: {
                    url: `data:image/jpeg;base64,${imageBase64}`,
                    detail: 'high'
                  }
                }
              ]
            }
          ],
          max_tokens: 2000,
          temperature: 0.1
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(`Vision API Error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`)
      }

      const data = await response.json()
      const content = data.choices?.[0]?.message?.content || ''

      // Detect content types
      const hasImages = content.includes('[HÌNH:') || content.includes('hình vẽ') || content.includes('đồ thị')
      const hasTables = content.includes('[BẢNG:') || content.includes('bảng')
      const hasFormulas = content.includes('[CÔNG THỨC:') || content.includes('=') || /[a-zA-Z]\s*=\s*/.test(content)

      console.log(`✅ Analyzed page ${pageNumber}: ${content.length} chars, images: ${hasImages}, tables: ${hasTables}, formulas: ${hasFormulas}`)

      return {
        pageNumber,
        content,
        hasImages,
        hasTables,
        hasFormulas
      }

    } catch (error) {
      console.error(`❌ Error analyzing page ${pageNumber}:`, error)
      return {
        pageNumber,
        content: `[Lỗi khi phân tích trang ${pageNumber}]`,
        hasImages: false,
        hasTables: false,
        hasFormulas: false
      }
    }
  }

  /**
   * Process PDF with Vision API (for scanned PDFs) - PARALLEL VERSION
   */
  async processPDFWithVision(
    file: File,
    maxPages: number = 10, // Giới hạn để tiết kiệm chi phí
    concurrentPages: number = 3 // Số trang xử lý đồng thời
  ): Promise<PDFVisionResult> {
    try {
      // Kiểm tra proxy URL thay vì API key
      const proxyUrl = API_CONFIG.openai.proxyUrl
      if (!proxyUrl) {
        return {
          success: false,
          error: 'OpenAI Proxy chưa được cấu hình'
        }
      }

      console.log('🚀 Processing PDF with GPT-4 Vision (PARALLEL MODE)...')
      console.log('📄 File:', file.name, `(${(file.size / 1024).toFixed(2)} KB)`)
      console.log('⚡ Concurrent pages:', concurrentPages)

      // Load PDF
      const arrayBuffer = await file.arrayBuffer()
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer })
      const pdf: PDFDocumentProxy = await loadingTask.promise

      console.log('📄 PDF loaded:', pdf.numPages, 'pages')

      // Process pages (giới hạn số trang để tiết kiệm chi phí)
      const pagesToProcess = Math.min(pdf.numPages, maxPages)
      if (pdf.numPages > maxPages) {
        console.warn(`⚠️ PDF có ${pdf.numPages} trang, chỉ xử lý ${maxPages} trang đầu (để tiết kiệm chi phí)`)
      }

      const pageContents: PageContent[] = []
      let totalTokens = 0

      // PARALLEL PROCESSING - Process multiple pages at once
      const startTime = Date.now()

      for (let batchStart = 1; batchStart <= pagesToProcess; batchStart += concurrentPages) {
        const batchEnd = Math.min(batchStart + concurrentPages - 1, pagesToProcess)
        const batchSize = batchEnd - batchStart + 1

        console.log(`📦 Batch processing pages ${batchStart}-${batchEnd} (${batchSize} pages in parallel)...`)

        // Create array of page numbers for this batch
        const pageNumbers = Array.from(
          { length: batchSize },
          (_, i) => batchStart + i
        )

        // Process all pages in batch in parallel
        const batchResults = await Promise.all(
          pageNumbers.map(async (pageNum) => {
            try {
              console.log(`🔄 [Page ${pageNum}] Converting to image...`)
              const imageBase64 = await this.convertPageToImage(pdf, pageNum, 2.0)

              console.log(`🤖 [Page ${pageNum}] Analyzing with Vision AI...`)
              const pageContent = await this.analyzePageWithVision(imageBase64, pageNum)

              console.log(`✅ [Page ${pageNum}] Complete: ${pageContent.content.length} chars`)
              return pageContent
            } catch (error) {
              console.error(`❌ [Page ${pageNum}] Error:`, error)
              return {
                pageNumber: pageNum,
                content: `[Lỗi khi xử lý trang ${pageNum}]`,
                hasImages: false,
                hasTables: false,
                hasFormulas: false
              }
            }
          })
        )

        // Add results to pageContents
        pageContents.push(...batchResults)

        // Calculate tokens for this batch
        batchResults.forEach(pageContent => {
          totalTokens += 765 + (pageContent.content.length / 4)
        })

        console.log(`✅ Batch ${batchStart}-${batchEnd} completed (${batchSize} pages)`)

        // Small delay between batches to avoid rate limits
        if (batchEnd < pagesToProcess) {
          await new Promise(resolve => setTimeout(resolve, 500))
        }
      }

      const endTime = Date.now()
      const totalTime = ((endTime - startTime) / 1000).toFixed(1)

      // Sort pages by page number (since parallel processing may return out of order)
      pageContents.sort((a, b) => a.pageNumber - b.pageNumber)

      // Combine all page contents
      const fullContent = pageContents
        .map(page => `\n=== TRANG ${page.pageNumber} ===\n${page.content}`)
        .join('\n\n')

      console.log('✅ Vision processing complete!')
      console.log('⚡ Total time:', totalTime, 'seconds')
      console.log('🚀 Speed:', (pagesToProcess / parseFloat(totalTime)).toFixed(1), 'pages/second')
      console.log('📊 Total content:', fullContent.length, 'characters')
      console.log('🪙 Estimated tokens:', Math.round(totalTokens))

      return {
        success: true,
        content: fullContent,
        pageContents,
        tokensUsed: totalTokens
      }

    } catch (error) {
      console.error('❌ Error processing PDF with Vision:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Có lỗi xảy ra khi xử lý PDF với Vision AI'
      }
    }
  }

  /**
   * Smart PDF processing: try text extraction first, fallback to Vision
   */
  async smartProcessPDF(
    file: File,
    textResult?: { success: boolean; text?: string }
  ): Promise<PDFVisionResult> {
    // If text extraction worked and has enough content, use it
    if (textResult?.success && textResult.text && textResult.text.length > 100) {
      console.log('✅ Using text extraction (faster & cheaper)')
      return {
        success: true,
        content: textResult.text
      }
    }

    // Otherwise, use Vision (for scanned PDFs)
    console.log('🔍 Text extraction failed or insufficient. Using Vision API (slower & more expensive)')
    return this.processPDFWithVision(file)
  }

  /**
   * Test Vision API connection
   * TODO: Refactor to use proxy
   */
  async testVisionAPI(): Promise<boolean> {
    console.warn('⚠️ testVisionAPI: Feature temporarily disabled - needs proxy implementation')
    return false

    /* TODO: Implement with proxy
    try {
      const proxyUrl = import.meta.env.VITE_OPENAI_PROXY_URL
      if (!proxyUrl) return false
      // Test with simple request
      return true
    } catch {
      return false
    }
    */
  }
}

// Export singleton instance
export const pdfVisionService = new PDFVisionService()
export default pdfVisionService

